'use client';

import { useRef } from 'react';

import {
  faArrowTurnDownLeft,
  faArrowUp,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'use-intl';

import { MAX_RESPONSE_LENGTH } from '@/shared-question-model';
import { useProjectResponseModeContext } from '@/shared-survey';
import {
  SharedUiComposer,
  WAVEFORM_PLAYER_CUSTOM_STOP_EVENT,
} from '@/shared-ui';
import { ProjectType } from '@/survey-graphql';

import { wrapOnChangeWithWaveformStop } from './project-response-open-end-send.helpers';
import { ProjectResponseSendComponentProps } from './project-response-renderer.type';
import { useProjectResponseSend } from './project-response-send.hooks';

export default function ProjectResponseOpenEndSendComponentDesktop({
  register,
  onKeyDown,
  isSubmitting,
  value,
  disabled,
  isConversationOver,
  type,
  isUserVoiceEnabled,
  hideNewLineText,
}: ProjectResponseSendComponentProps) {
  const tChat = useTranslations('Chat');
  const tUserTest = useTranslations('UserTest');

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const { handleToggle } = useProjectResponseSend(type);
  const { projectType, responseMode } = useProjectResponseModeContext();

  const onToggleVoice = () => {
    const wasVoice = responseMode === 'voice';
    handleToggle();
    if (wasVoice && !isConversationOver) {
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  };

  const reg = register?.('content');

  const setTextareaRef = (el: HTMLTextAreaElement | null) => {
    reg?.ref(el);
    textareaRef.current = el;
  };

  const regProps = reg
    ? {
        name: reg.name,
        onChange: wrapOnChangeWithWaveformStop(reg.onChange, () =>
          window.dispatchEvent(new Event(WAVEFORM_PLAYER_CUSTOM_STOP_EVENT))
        ),
        onBlur: reg.onBlur,
      }
    : undefined;

  const submitLabel =
    projectType === ProjectType.UsabilityTesting
      ? tUserTest('continue')
      : tChat('send');

  const newLineHint = hideNewLineText ? null : (
    <div
      className='flex items-center gap-1 text-nowrap'
      data-test='open-end-newline-hint'
    >
      <div className='flex h-6 w-8 items-start rounded-lg border-2 border-gray-100'>
        <div className='flex h-4 w-full items-center justify-center rounded-xs bg-gray-100'>
          <FontAwesomeIcon
            icon={faArrowUp}
            size='2xs'
            aria-hidden='true'
            className='font-semibold text-black'
          />
        </div>
      </div>
      <div className='flex h-6 w-8 items-start rounded-lg border-2 border-gray-100'>
        <div className='flex h-4 w-full items-center justify-center rounded-xs bg-gray-100'>
          <FontAwesomeIcon
            icon={faArrowTurnDownLeft}
            size='2xs'
            aria-hidden='true'
            className='font-semibold text-black'
          />
        </div>
      </div>
      <div className='[font-family:var(--brand-font)] text-xs text-gray-400'>
        {tChat('newLineHint')}
      </div>
    </div>
  );

  return (
    /* oxlint-disable jsx-a11y/no-autofocus */
    <SharedUiComposer
      size='default'
      type='write'
      data-test='open-end-send-desktop'
      submitType='submit'
      placeholder={tChat('inputPlaceholder')}
      maxLength={MAX_RESPONSE_LENGTH}
      autoFocus
      textareaAriaLabel={tChat('textareaAriaLabel')}
      textareaRef={setTextareaRef}
      textareaDisabled={isConversationOver}
      textareaProps={regProps}
      onKeyDown={isSubmitting ? undefined : onKeyDown}
      canSubmit={Boolean(value?.trim())}
      loading={isSubmitting}
      disabled={disabled}
      submitLabel={submitLabel}
      submitAriaLabel={submitLabel}
      submitClassName='bg-(--brand-primary) text-(--brand-primary-contrast) [&_svg]:text-(--brand-primary-contrast) enabled:hover:bg-(--brand-primary) disabled:bg-(--brand-primary) disabled:opacity-20'
      isVoiceEnabled={isUserVoiceEnabled}
      onToggleVoice={onToggleVoice}
      voiceToggleDisabled={isConversationOver}
      voiceToggleAriaLabel={tChat('voiceToggleAriaLabel')}
      footerStart={newLineHint}
    />
    /* oxlint-enable jsx-a11y/no-autofocus */
  );
}
